import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import EmptyState from "components/EmptyState";
import { requireAuth } from "utilities/auth";
import { useNotificationEvents } from "hooks/crm/useNotificationAdmin";
import { Radio, Search, Mail, Send, Slack, Webhook, Bell } from "lucide-react";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "telegram", label: "Telegram", icon: Send },
  { key: "slack", label: "Slack", icon: Slack },
  { key: "webhook", label: "Webhook", icon: Webhook },
  { key: "inApp", label: "In-app", icon: Bell },
];

/**
 * NotificationConfig (SOUND-523) — the admin event catalog (grouped by domain).
 * Each row summarizes routing (enabled channels, audience, active) and opens the
 * per-event editor. The admin analog of the workflow-builder catalog; distinct
 * from the Phase-2 per-user prefs page at /settings/notifications.
 */
function NotificationConfig() {
  const navigate = useNavigate();
  const { data: events = [], isLoading, isError } = useNotificationEvents();
  const [search, setSearch] = useState("");

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = events.filter(
      (e) =>
        !q ||
        (e.label || "").toLowerCase().includes(q) ||
        (e.eventKey || "").toLowerCase().includes(q)
    );
    const byDomain = {};
    filtered.forEach((e) => {
      const d = e.domain || "other";
      if (!byDomain[d]) byDomain[d] = [];
      byDomain[d].push(e);
    });
    return Object.entries(byDomain);
  }, [events, search]);

  return (
    <div>
      <PageHeader
        title="Notification Config"
        subtitle="Admin routing + template authoring. Which events fire on which channels, to which audience — and the message per channel. (Per-user delivery toggles live under Notifications.)"
      />

      <div className="container space-y-5 py-6">
        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input pl-9"
            placeholder="Search events…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="space-y-3">
            <Skeleton.Rect className="h-14 w-full" />
            <Skeleton.Rect className="h-14 w-full" />
            <Skeleton.Rect className="h-14 w-2/3" />
          </div>
        ) : isError ? (
          <div className="surface p-8 text-center text-destructive">Failed to load notification events.</div>
        ) : groups.length === 0 ? (
          <EmptyState icon={Radio} heading="No notification events" description="No events match your search." />
        ) : (
          <div className="space-y-6">
            {groups.map(([domain, evs]) => (
              <section key={domain} className="surface overflow-hidden rounded-xl ring-1 ring-border">
                <div className="border-b border-border px-4 py-2.5">
                  <h3 className="text-sm font-semibold capitalize text-foreground">{domain}</h3>
                </div>
                <div className="divide-y divide-border">
                  {evs.map((e) => (
                    <button
                      key={e.eventKey}
                      type="button"
                      onClick={() => navigate(`/settings/notification-config/${encodeURIComponent(e.eventKey)}`)}
                      className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-muted"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium text-foreground">{e.label || e.eventKey}</span>
                          {!e.active && <span className="badge badge-muted">Inactive</span>}
                        </div>
                        <div className="truncate font-mono text-xs text-muted-foreground">{e.eventKey}</div>
                      </div>
                      <div className="hidden items-center gap-1.5 sm:flex">
                        {CHANNELS.map(({ key, label, icon: Icon }) => {
                          const on = e.channels?.[key]?.enabled;
                          return (
                            <span
                              key={key}
                              title={`${label}${on ? " (on)" : " (off)"}`}
                              className={`grid h-6 w-6 place-items-center rounded ${
                                on ? "bg-brand/15 text-brand" : "text-muted-foreground/40"
                              }`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </span>
                          );
                        })}
                      </div>
                      <div className="hidden w-28 shrink-0 text-right text-xs capitalize text-muted-foreground md:block">
                        {Array.isArray(e.defaultAudience) && e.defaultAudience.length
                          ? e.defaultAudience.join(", ")
                          : "—"}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default requireAuth(NotificationConfig);
